import { ClientMessage, ServerMessage, RunState, Player, CombatEvent, LootDrop, ClassName, SaveData, DeltaState } from 'shared';
import { WS_CONFIG } from 'shared';
import { WS_URL } from '../config';

const SAVE_KEY_PREFIX = 'dungeon_link_save_';
const SAVE_INDEX_KEY = 'dungeon_link_saves_index';
const SAVE_VERSION = 1;
const MAX_SAVE_SLOTS = 5;

type MessageHandler = (message: ServerMessage) => void;
type ConnectionHandler = () => void;

// Set to true to enable verbose debug logging (CAUSES LAG - only for debugging)
const DEBUG_LOGGING = false;

/**
 * Connection state machine to prevent race conditions.
 * See docs/BUG_FIXES_2026_02.md "WebSocket Connection State Machine" for context.
 *
 * Valid transitions:
 *   DISCONNECTED -> CONNECTING (via connect())
 *   CONNECTING -> CONNECTED (via onopen)
 *   CONNECTING -> DISCONNECTED (via onerror/onclose or disconnect())
 *   CONNECTED -> RECONNECTING (via onclose when not intentionally disconnected)
 *   CONNECTED -> DISCONNECTED (via disconnect())
 *   RECONNECTING -> CONNECTING (via reconnect timer)
 *   RECONNECTING -> DISCONNECTED (via disconnect() or max attempts)
 */
type ConnectionState = 'DISCONNECTED' | 'CONNECTING' | 'CONNECTED' | 'RECONNECTING';

export class WebSocketClient {
  private ws: WebSocket | null = null;
  private messageHandlers: Set<MessageHandler> = new Set();
  private connectHandlers: Set<ConnectionHandler> = new Set();
  private disconnectHandlers: Set<ConnectionHandler> = new Set();
  private reconnectAttempts = 0;
  private reconnectTimeout: number | null = null;
  private pingInterval: number | null = null;
  private connectionState: ConnectionState = 'DISCONNECTED';
  // Flag to distinguish intentional disconnect from unexpected close
  private intentionalDisconnect = false;

  // State deduplication - prevents processing identical updates
  // See docs/PERFORMANCE-STATE-UPDATES.md for context
  private lastStateHash: string = '';
  private duplicateCount: number = 0;

  // Game state received from server
  public runId: string | null = null;
  public playerId: string | null = null;
  public currentState: RunState | null = null;
  public isConnected = false;

  // Lives tracking (5 max, character deleted at 0)
  public currentLives: number = 5;
  public currentSaveSlot: number | null = null;

  constructor() {}

  /**
   * Get the current connection state (for testing/debugging)
   */
  getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  /**
   * Connect to the WebSocket server.
   * Uses WS_URL from config which respects environment variables.
   *
   * Returns a Promise that resolves on successful connection.
   * If already connecting/connected, returns immediately.
   */
  connect(): Promise<void> {
    // Guard: prevent double-connect
    if (this.connectionState === 'CONNECTING' || this.connectionState === 'CONNECTED') {
      return Promise.resolve();
    }

    this.connectionState = 'CONNECTING';
    this.intentionalDisconnect = false;

    return new Promise((resolve, reject) => {
      try {
        console.log(`[WS] Connecting to ${WS_URL}`);
        this.ws = new WebSocket(WS_URL);
        let settled = false;

        this.ws.onopen = () => {
          if (settled) return;
          settled = true;
          console.log('Connected to server');
          this.connectionState = 'CONNECTED';
          this.isConnected = true;
          this.reconnectAttempts = 0;

          // Client-side keepalive ping every 15s
          // Prevents Railway/Cloudflare proxy idle timeout
          if (this.pingInterval) clearInterval(this.pingInterval);
          this.pingInterval = window.setInterval(() => {
            if (this.connectionState === 'CONNECTED') {
              this.send({ type: 'PING' });
            }
          }, 15000);

          this.connectHandlers.forEach(h => h());
          resolve();
        };

        this.ws.onmessage = (event) => {
          // Guard: don't process messages if we've been intentionally disconnected
          if (this.connectionState === 'DISCONNECTED') return;
          try {
            const message = JSON.parse(event.data) as ServerMessage;
            this.handleMessage(message);
          } catch (e) {
            console.error('Failed to parse message:', e);
          }
        };

        this.ws.onclose = () => {
          const wasConnected = this.connectionState === 'CONNECTED';
          // Only transition if not already DISCONNECTED (intentional disconnect)
          if (this.connectionState === 'DISCONNECTED') return;

          this.isConnected = false;

          if (this.intentionalDisconnect) {
            // Intentional disconnect — don't reconnect, don't fire handlers again
            this.connectionState = 'DISCONNECTED';
            return;
          }

          if (wasConnected) {
            // Was connected, lost connection — notify handlers and try to reconnect
            console.log('Disconnected from server');
            this.connectionState = 'RECONNECTING';
            this.disconnectHandlers.forEach(h => h());
            this.attemptReconnect();
          } else {
            // Failed during CONNECTING — reject the promise, don't auto-reconnect
            // (the caller, e.g. BootScene, should handle retry)
            this.connectionState = 'DISCONNECTED';
            if (!settled) {
              settled = true;
              reject(new Error('WebSocket connection failed'));
            }
          }
        };

        this.ws.onerror = (_error) => {
          // onerror is always followed by onclose. We use onclose for state transitions.
          // Only reject here if onclose hasn't already handled it.
          if (!settled) {
            settled = true;
            // Don't reject here — let onclose handle the state transition and rejection.
            // This prevents double-fire of onerror+onclose causing issues.
          }
        };
      } catch (e) {
        this.connectionState = 'DISCONNECTED';
        reject(e);
      }
    });
  }

  /**
   * Disconnect from server intentionally.
   * Stops reconnection attempts and cleans up the socket.
   */
  disconnect(): void {
    this.intentionalDisconnect = true;
    this.connectionState = 'DISCONNECTED';
    this.isConnected = false;

    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.ws) {
      this.ws.onclose = null; // Prevent onclose handler from firing
      this.ws.onerror = null;
      this.ws.onmessage = null;
      this.ws.close();
      this.ws = null;
    }
  }

  /**
   * Attempt to reconnect with exponential backoff.
   * Only runs when connectionState is RECONNECTING.
   */
  private attemptReconnect(): void {
    if (this.connectionState !== 'RECONNECTING') return;
    if (this.reconnectAttempts >= WS_CONFIG.MAX_RECONNECT_ATTEMPTS) {
      console.log('Max reconnect attempts reached');
      this.connectionState = 'DISCONNECTED';
      return;
    }

    this.reconnectAttempts++;
    console.log(`Attempting to reconnect (${this.reconnectAttempts}/${WS_CONFIG.MAX_RECONNECT_ATTEMPTS})...`);

    this.reconnectTimeout = window.setTimeout(() => {
      if (this.connectionState !== 'RECONNECTING') return;
      // Transition back to allow connect() to proceed
      this.connectionState = 'DISCONNECTED';
      this.connect().then(() => {
        // Reconnected successfully — but the server doesn't know who we are.
        // The old run was cleaned up server-side on disconnect.
        // Signal to the UI that we need to return to menu.
        console.log('[WS] Reconnected, but run state is lost. Returning to menu.');
        this.runId = null;
        this.playerId = null;
        this.currentState = null;
      }).catch(() => {
        // Failed to reconnect — try again
        if (this.connectionState === 'CONNECTED') return; // shouldn't happen
        this.connectionState = 'RECONNECTING';
        this.attemptReconnect();
      });
    }, WS_CONFIG.RECONNECT_DELAY * this.reconnectAttempts);
  }

  /**
   * Handle incoming message
   */
  private handleMessage(message: ServerMessage): void {
    switch (message.type) {
      case 'RUN_CREATED':
        this.runId = message.runId;
        this.currentState = message.state;
        if (this.currentState.players.length > 0) {
          this.playerId = this.currentState.players[0].id;
        }
        break;

      case 'RUN_JOINED':
        this.playerId = message.playerId;
        this.currentState = message.state;
        break;

      case 'STATE_UPDATE':
        // Full state update - replace entire state (used for initial sync and floor changes)
        // Deduplicate state updates to prevent unnecessary processing
        const stateHash = this.computeStateHash(message.state);
        if (stateHash === this.lastStateHash) {
          this.duplicateCount++;
          if (DEBUG_LOGGING) {
            console.log(`[WS] Skipped duplicate STATE_UPDATE (${this.duplicateCount} duplicates)`);
          }
          return; // Skip processing, don't notify handlers
        }
        this.lastStateHash = stateHash;
        this.duplicateCount = 0;
        this.currentState = message.state;
        if (DEBUG_LOGGING) {
          console.log(`[WS] Full state update: ${JSON.stringify(message.state).length} bytes`);
        }
        break;

      case 'DELTA_UPDATE':
        // Delta update - merge with cached state (much smaller payload)
        if (this.currentState) {
          this.applyDeltaUpdate(message.delta);
          if (DEBUG_LOGGING) {
            console.log(`[WS] Delta update: ${JSON.stringify(message.delta).length} bytes`);
          }
        }
        break;

      case 'PLAYER_JOINED':
        if (this.currentState) {
          this.currentState.players.push(message.player);
        }
        break;

      case 'PLAYER_LEFT':
        if (this.currentState) {
          this.currentState.players = this.currentState.players.filter(
            p => p.id !== message.playerId
          );
        }
        break;
    }

    // Notify all handlers
    this.messageHandlers.forEach(handler => handler(message));
  }

  /**
   * Send a message to the server
   */
  send(message: ClientMessage): void {
    if (DEBUG_LOGGING) console.log('[DEBUG] Sending message:', message.type, 'ws state:', this.ws?.readyState);
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
      if (DEBUG_LOGGING) console.log('[DEBUG] Message sent successfully');
    } else {
      console.error('[DEBUG] Failed to send - ws not open. readyState:', this.ws?.readyState);
    }
  }

  /**
   * Create a new run
   */
  createRun(playerName: string, classId: ClassName): void {
    // Reset lives and save slot for new character
    this.currentLives = 5;
    this.currentSaveSlot = null;
    // Reset state hash to ensure first state update is processed
    this.lastStateHash = '';
    this.duplicateCount = 0;
    this.send({ type: 'CREATE_RUN', playerName, classId });
  }

  // NOTE: joinRun removed - game is now single-player only

  /**
   * Send player input
   */
  sendInput(moveX: number, moveY: number, castAbility?: string, targetId?: string): void {
    this.send({
      type: 'PLAYER_INPUT',
      input: { moveX, moveY, castAbility, targetId }
    });
  }

  /**
   * Set current target
   */
  setTarget(targetId: string | null): void {
    this.send({ type: 'SET_TARGET', targetId });
  }

  /**
   * Advance to next floor
   */
  advanceFloor(): void {
    this.send({ type: 'ADVANCE_FLOOR' });
  }

  /**
   * Use an item (potion) from backpack
   */
  useItem(itemId: string): void {
    this.send({ type: 'USE_ITEM', itemId });
  }

  /**
   * Swap equipment with backpack item
   */
  swapEquipment(backpackIndex: number, slot: import('shared').EquipSlot): void {
    this.send({ type: 'SWAP_EQUIPMENT', backpackIndex, slot });
  }

  /**
   * Unequip item to backpack
   */
  unequipItem(slot: import('shared').EquipSlot): void {
    this.send({ type: 'UNEQUIP_ITEM', slot });
  }

  /**
   * Interact with a vendor NPC
   */
  interactVendor(vendorId: string): void {
    this.send({ type: 'INTERACT_VENDOR', vendorId });
  }

  /**
   * Purchase a service from a vendor
   */
  purchaseService(vendorId: string, serviceType: 'level_up' | 'train_ability', abilityId?: string): void {
    this.send({ type: 'PURCHASE_SERVICE', vendorId, serviceType, abilityId });
  }

  /**
   * Send ping
   */
  ping(): void {
    this.send({ type: 'PING' });
  }

  /**
   * Pickup a ground item by clicking on it
   */
  pickupGroundItem(itemId: string): void {
    this.send({ type: 'PICKUP_GROUND_ITEM', itemId });
  }

  /**
   * Register message handler
   */
  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler);
    if (DEBUG_LOGGING) console.log('[WS] Message handler added, total handlers:', this.messageHandlers.size);
    return () => {
      this.messageHandlers.delete(handler);
      if (DEBUG_LOGGING) console.log('[WS] Message handler removed, total handlers:', this.messageHandlers.size);
    };
  }

  /**
   * Register connect handler
   */
  onConnect(handler: ConnectionHandler): () => void {
    this.connectHandlers.add(handler);
    return () => this.connectHandlers.delete(handler);
  }

  /**
   * Register disconnect handler
   */
  onDisconnect(handler: ConnectionHandler): () => void {
    this.disconnectHandlers.add(handler);
    return () => this.disconnectHandlers.delete(handler);
  }

  /**
   * Get current player from state
   */
  getCurrentPlayer(): Player | null {
    if (!this.currentState || !this.playerId) return null;
    return this.currentState.players.find(p => p.id === this.playerId) ?? null;
  }

  // NOTE: getInviteLink removed - game is now single-player only

  /**
   * Save current character to a slot
   */
  saveCharacter(slot?: number): boolean {
    const player = this.getCurrentPlayer();
    if (!player || !this.currentState) return false;

    // Use provided slot, or current save slot (if loaded from save), or find empty/oldest slot
    const targetSlot = slot ?? this.currentSaveSlot ?? this.findEmptySlot() ?? this.findOldestSlot();

    // Get existing lives count or default to 5
    const existingLives = this.currentLives ?? 5;

    // Ensure player name is not empty
    const playerName = player.name || 'Hero';
    if (!player.name) {
      console.warn('[SAVE] Warning: Player name was empty, using fallback "Hero"');
    }

    const saveData: SaveData = {
      version: SAVE_VERSION,
      timestamp: Date.now(),
      playerName: playerName,
      classId: player.classId,
      level: player.level,
      xp: player.xp,
      xpToNextLevel: player.xpToNextLevel,
      gold: player.gold,
      rerollTokens: player.rerollTokens,
      baseStats: { ...player.baseStats },
      equipment: player.equipment,
      abilities: player.abilities.map(a => ({ ...a })),
      backpack: [...player.backpack],
      highestFloor: this.currentState.floor,
      lives: existingLives
    };

    try {
      localStorage.setItem(`${SAVE_KEY_PREFIX}${targetSlot}`, JSON.stringify(saveData));
      this.updateSaveIndex(targetSlot);
      // Remember this slot for future saves
      this.currentSaveSlot = targetSlot;
      console.log(`[SAVE] Character saved to slot ${targetSlot}: ${player.name} (Level ${player.level}, Floor ${this.currentState.floor})`);
      return true;
    } catch (e) {
      console.error('Failed to save character:', e);
      return false;
    }
  }

  /**
   * Find first empty save slot
   */
  private findEmptySlot(): number | null {
    for (let i = 0; i < MAX_SAVE_SLOTS; i++) {
      if (!localStorage.getItem(`${SAVE_KEY_PREFIX}${i}`)) {
        return i;
      }
    }
    return null;
  }

  /**
   * Find oldest save slot to overwrite
   */
  private findOldestSlot(): number {
    let oldestSlot = 0;
    let oldestTime = Infinity;

    for (let i = 0; i < MAX_SAVE_SLOTS; i++) {
      const data = localStorage.getItem(`${SAVE_KEY_PREFIX}${i}`);
      if (data) {
        try {
          const save = JSON.parse(data) as SaveData;
          if (save.timestamp < oldestTime) {
            oldestTime = save.timestamp;
            oldestSlot = i;
          }
        } catch {
          return i; // Corrupted slot, use it
        }
      }
    }
    return oldestSlot;
  }

  /**
   * Update save index
   */
  private updateSaveIndex(slot: number): void {
    try {
      const indexData = localStorage.getItem(SAVE_INDEX_KEY);
      const index: number[] = indexData ? JSON.parse(indexData) : [];
      if (!index.includes(slot)) {
        index.push(slot);
      }
      localStorage.setItem(SAVE_INDEX_KEY, JSON.stringify(index));
    } catch {
      localStorage.setItem(SAVE_INDEX_KEY, JSON.stringify([slot]));
    }
  }

  /**
   * Get all saved characters
   */
  getAllSavedCharacters(): { slot: number; data: SaveData }[] {
    const saves: { slot: number; data: SaveData }[] = [];

    for (let i = 0; i < MAX_SAVE_SLOTS; i++) {
      const data = localStorage.getItem(`${SAVE_KEY_PREFIX}${i}`);
      if (data) {
        try {
          const saveData = JSON.parse(data) as SaveData;
          if (saveData.version === SAVE_VERSION) {
            saves.push({ slot: i, data: saveData });
          }
        } catch {
          // Skip corrupted saves
        }
      }
    }

    // Sort by timestamp (newest first)
    saves.sort((a, b) => b.data.timestamp - a.data.timestamp);
    return saves;
  }

  /**
   * Load saved character data from a specific slot
   */
  loadCharacter(slot: number): SaveData | null {
    try {
      const data = localStorage.getItem(`${SAVE_KEY_PREFIX}${slot}`);
      if (!data) return null;

      const saveData = JSON.parse(data) as SaveData;

      if (saveData.version !== SAVE_VERSION) {
        console.warn('Save data version mismatch');
        return null;
      }

      return saveData;
    } catch (e) {
      console.error('Failed to load character:', e);
      return null;
    }
  }

  /**
   * Check if any saved characters exist
   */
  hasSavedCharacters(): boolean {
    return this.getAllSavedCharacters().length > 0;
  }

  /**
   * Delete saved character from a slot
   */
  deleteSavedCharacter(slot: number): void {
    localStorage.removeItem(`${SAVE_KEY_PREFIX}${slot}`);
  }

  /**
   * Create a run from saved character
   */
  createRunFromSave(saveData: SaveData, slot: number): void {
    // Store the save slot and lives for tracking
    this.currentSaveSlot = slot;
    this.currentLives = saveData.lives ?? 5; // Default to 5 for old saves
    // Reset state hash to ensure first state update is processed
    this.lastStateHash = '';
    this.duplicateCount = 0;
    this.send({ type: 'CREATE_RUN_FROM_SAVE', saveData });
  }

  /**
   * Handle player death - decrement lives and update save
   * Returns true if character is deleted (out of lives)
   */
  handlePlayerDeath(): boolean {
    this.currentLives = Math.max(0, this.currentLives - 1);
    console.log(`[LIVES] Player died. Lives remaining: ${this.currentLives}`);

    if (this.currentLives <= 0) {
      // Delete the character
      if (this.currentSaveSlot !== null) {
        console.log(`[LIVES] Character out of lives! Deleting from slot ${this.currentSaveSlot}`);
        this.deleteSavedCharacter(this.currentSaveSlot);
        this.currentSaveSlot = null;
      }
      return true; // Character deleted
    }

    // Auto-save with updated lives
    if (this.currentSaveSlot !== null) {
      this.saveCharacter(this.currentSaveSlot);
    }

    return false; // Character still has lives
  }

  /**
   * Get current lives count
   */
  getLives(): number {
    return this.currentLives;
  }

  /**
   * Clear all message handlers
   * IMPORTANT: Call this before destroying the Phaser game to prevent handler accumulation
   */
  clearAllHandlers(): void {
    console.log('[WS] Clearing all handlers. Count was:', this.messageHandlers.size);
    this.messageHandlers.clear();
    this.connectHandlers.clear();
    this.disconnectHandlers.clear();
  }

  /**
   * Apply delta update to cached state.
   * Merges dynamic data from delta with static structure from initial sync.
   * This allows us to receive ~500-1000 byte updates instead of ~10KB.
   */
  private applyDeltaUpdate(delta: DeltaState): void {
    if (!this.currentState) return;

    // FIRST: Add any newly spawned enemies to the cached state
    // This must happen before updating enemies so the new enemies exist in the room
    if (delta.newEnemies && delta.newEnemies.length > 0) {
      for (const newEnemy of delta.newEnemies) {
        const room = this.currentState.dungeon.rooms.find(r => r.id === newEnemy.roomId);
        if (room) {
          // Check if enemy doesn't already exist (shouldn't, but be safe)
          if (!room.enemies.find(e => e.id === newEnemy.enemy.id)) {
            room.enemies.push(newEnemy.enemy);
            if (DEBUG_LOGGING) {
              console.log(`[WS] Added new enemy: ${newEnemy.enemy.name} (${newEnemy.enemy.id}) to room ${newEnemy.roomId}`);
            }
          }
        }
      }
    }

    // Update players
    for (const deltaPlayer of delta.players) {
      const player = this.currentState.players.find(p => p.id === deltaPlayer.id);
      if (player) {
        player.position = deltaPlayer.position;
        player.stats.health = deltaPlayer.health;
        player.stats.maxHealth = deltaPlayer.maxHealth;
        player.stats.mana = deltaPlayer.mana;
        player.stats.maxMana = deltaPlayer.maxMana;
        player.isAlive = deltaPlayer.isAlive;
        player.targetId = deltaPlayer.targetId;
        player.gold = deltaPlayer.gold;
        player.xp = deltaPlayer.xp;
        player.xpToNextLevel = deltaPlayer.xpToNextLevel;
        player.level = deltaPlayer.level;
        player.rerollTokens = deltaPlayer.rerollTokens;
        player.baseStats = deltaPlayer.baseStats;
        player.buffs = deltaPlayer.buffs;
        // Update abilities (includes new abilities learned from vendors)
        player.abilities = deltaPlayer.abilities;
        // Update inventory (items and potions picked up)
        player.backpack = deltaPlayer.backpack;
        // Update equipment (from vendor swaps, etc.)
        player.equipment = deltaPlayer.equipment;
      }
    }

    // Update pets
    for (const deltaPet of delta.pets) {
      const pet = this.currentState.pets.find(p => p.id === deltaPet.id);
      if (pet) {
        pet.position = deltaPet.position;
        pet.stats.health = deltaPet.health;
        pet.stats.maxHealth = deltaPet.maxHealth;
        pet.isAlive = deltaPet.isAlive;
        pet.targetId = deltaPet.targetId;
      }
    }

    // Update enemies (now includes newly added enemies from above)
    // Search ALL rooms for the enemy because patrol enemies can move between rooms
    // and the client's cached room might differ from the server's current room
    for (const deltaEnemy of delta.enemies) {
      let enemyFound = false;
      for (const room of this.currentState.dungeon.rooms) {
        const enemy = room.enemies.find(e => e.id === deltaEnemy.id);
        if (enemy) {
          enemy.position = deltaEnemy.position;
          enemy.stats.health = deltaEnemy.health;
          enemy.stats.maxHealth = deltaEnemy.maxHealth;
          enemy.isAlive = deltaEnemy.isAlive;
          enemy.targetId = deltaEnemy.targetId;
          enemy.isHidden = deltaEnemy.isHidden;
          enemy.debuffs = deltaEnemy.debuffs;
          enemy.isEnraged = deltaEnemy.isEnraged;
          enemy.isInvulnerable = deltaEnemy.isInvulnerable;
          enemy.isRegenerating = deltaEnemy.isRegenerating;
          enemyFound = true;
          break; // Found the enemy, no need to search other rooms
        }
      }
    }

    // Update room cleared status, ground items, and traps
    for (const deltaRoom of delta.rooms) {
      const room = this.currentState.dungeon.rooms.find(r => r.id === deltaRoom.id);
      if (room) {
        room.cleared = deltaRoom.cleared;
        room.groundItems = deltaRoom.groundItems;
        room.traps = deltaRoom.traps;
      }
    }

    // Update chest status
    for (const deltaChest of delta.chests) {
      for (const room of this.currentState.dungeon.rooms) {
        const chest = room.chests?.find(c => c.id === deltaChest.id);
        if (chest) {
          chest.isOpen = deltaChest.isOpen;
          break;
        }
      }
    }

    // Update other dynamic fields
    this.currentState.groundEffects = delta.groundEffects;
    this.currentState.inCombat = delta.inCombat;
    this.currentState.dungeon.currentRoomId = delta.currentRoomId;
    this.currentState.dungeon.bossDefeated = delta.bossDefeated;
    this.currentState.pendingLoot = delta.pendingLoot;
  }

  /**
   * Compute a lightweight hash of the game state for deduplication.
   * Focuses on frequently-changing data to detect meaningful changes.
   * See docs/PERFORMANCE-STATE-UPDATES.md for context.
   */
  /**
   * Compute a lightweight hash of the game state for deduplication.
   * Covers all fields that can change between updates to prevent
   * dropping meaningful STATE_UPDATEs.
   * See docs/BUG_FIXES_2026_02.md "State Hash Improvements" for context.
   */
  private computeStateHash(state: RunState): string {
    const parts: string[] = [
      state.runId,
      String(state.floor),
      String(state.inCombat),
      state.dungeon?.currentRoomId || '',
      String(state.dungeon?.bossDefeated || false),
      String(state.groundEffects?.length || 0)
    ];

    // Hash player data — include all dynamic fields
    if (state.players) {
      for (const player of state.players) {
        parts.push(
          player.id,
          String(Math.round(player.position.x)),
          String(Math.round(player.position.y)),
          String(Math.round(player.stats.health)),
          String(Math.round(player.stats.mana)),
          String(player.isAlive),
          player.targetId || '',
          String(player.gold),
          String(player.level),
          String(player.xp),
          // Include ability ranks, not just count
          (player.abilities || []).map(a => `${a.abilityId}:${a.rank}`).join(','),
          // Include backpack item IDs
          (player.backpack || []).map(i => i.id).join(','),
          // Include equipment IDs for swap detection
          Object.values(player.equipment || {}).map(e => e?.id || '').join(','),
          // Include buff count and IDs for boss phase/modifier detection
          (player.buffs || []).map(b => b.id).join(',')
        );
      }
    }

    // Hash enemy states in current room + boss flags
    const currentRoom = state.dungeon?.rooms?.find(r => r.id === state.dungeon.currentRoomId);
    if (currentRoom?.enemies) {
      for (const enemy of currentRoom.enemies) {
        parts.push(
          enemy.id,
          String(Math.round(enemy.position.x)),
          String(Math.round(enemy.position.y)),
          String(Math.round(enemy.stats.health)),
          String(enemy.isAlive),
          String(enemy.isEnraged || false),
          String(enemy.isInvulnerable || false)
        );
      }
      // Include chest states for current room
      if (currentRoom.chests) {
        for (const chest of currentRoom.chests) {
          parts.push(chest.id, String(chest.isOpen));
        }
      }
    }

    return parts.join('|');
  }
}

// Singleton instance
export const wsClient = new WebSocketClient();
